import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Button, Container } from "reactstrap";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import styles from "Pages/Admin/AdminConfiguration/AdminConfiguration.module.css";
import { format } from "date-fns";
import usePageTitle from "Hooks/usePageTitle";

export default function StuckIntegrations() {
    const [integrations, setIntegrations] = useState<IMessageHeader[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [allRowsSelected, setAllRowsSelected] = useState<any>(false);

    const { get, post } = useFetch();
    const { newAlert } = useAlertContext();

    usePageTitle("Unfinished Integrations");
    const ignore = useRef(false);

    const getStuckIntegrations = useCallback(
        async (ignore: React.MutableRefObject<boolean>) => {
            if (ignore.current) return;
            setIsLoading(true);

            const response = await get(`api/admin/stuckintegrations`);

            let data: Response | IMessageHeader[];
            try {
                data = await response.json();
                console.log(data);
            } catch (e) {
                data = response;
            }
            if (response.ok && !ignore.current) {
                data = data as IMessageHeader[];
                setIntegrations(data);
            }
            setIsLoading(false);
        },
        [get]
    );

    useEffect(() => {
        ignore.current = false;

        getStuckIntegrations(ignore);

        return () => {
            ignore.current = true;
        };
    }, [get, getStuckIntegrations, post]);

    const onSetSelectedRows = useCallback((row: any) => {
        setSelectedRows((prev) => {
            const ids = prev.map((r) => r?.id);
            if (ids?.includes(row?.original.id)) {
                return prev.filter((r) => r?.id !== row?.original.id);
            } else {
                return [...prev, row.original];
            }
        });
    }, []);

    const toggleAllRowsSelected = () => {
        setAllRowsSelected((prev: boolean) => !prev);
    };

    const onToggleAllRowsSelected = useCallback((rowObjects: any) => {
        const rows = rowObjects.map((row: any) => row.original);
        setSelectedRows(rows);
    }, []);

    async function abandonIntegrations() {
        const bodyAsIds = selectedRows.map((row) => row?.id);
        const url = `/api/admin/stuckintegrations/abandon`;
        const response = await post(url, bodyAsIds);

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Abandon stuck integrations reponse: ", data);

        if (response.ok) {
            getStuckIntegrations(ignore);
            newAlert({
                message: `Successfully abandoned integration(s)`,
                color: "success",
            });
        } else {
            newAlert({
                message: `Error abandoning integrations: ${data.detail}`,
                color: "danger",
            });
        }
        setSelectedRows([]);
        setAllRowsSelected(false);
    }

    const selectAllBtnLabel = allRowsSelected
        ? "Clear Selection"
        : "Select All";
    const selectAllBtnColor = allRowsSelected ? "secondary" : "primary";

    const columns = useMemo(() => {
        const cellContent = (value: string) => {
            return <span className={styles.textBox}>{value}</span>;
        };
        return [
            {
                Header: "Start Time",
                accessor: "start_time",
                Cell: ({ value }: { value: string }) =>
                    cellContent(
                        value
                            ? format(new Date(value), "MMM d, yyyy, HH:mm:ss")
                            : "null"
                    ),
            },
            {
                Header: "Company ID",
                accessor: "company_id",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Initiator",
                accessor: "initiator",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Machine",
                accessor: "machine_name",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Name",
                accessor: "name",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },

            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }: { value: string }) => cellContent(value),
            },
            {
                Header: "Finish Time",
                accessor: "finish_time",
                Cell: ({ value }: { value: string }) =>
                    cellContent(
                        value
                            ? format(new Date(value), "MMM d, yyyy, HH:mm:ss")
                            : "null"
                    ),
            },
        ];
    }, []);

    const tableProps = {
        columns,
        data: integrations,
        showPagination: true,
        onSetSelectedRows,
        allRowsSelected,
        onToggleAllRowsSelected,
        selectedRows,
        isLoading,
    };

    return (
        <Container fluid>
            {/* Header and Select/Abandon buttons */}
            <div className={styles.headerAndButtonsBox}>
                <h1>Unfinished Integrations</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="danger"
                        onClick={(e) => abandonIntegrations()}
                    >
                        Abandon
                    </Button>
                    <Button
                        color={selectAllBtnColor}
                        onClick={toggleAllRowsSelected}
                    >
                        {selectAllBtnLabel}
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />
        </Container>
    );
}
